// src/lib/apiError.ts
/**
 * ترجمة رسائل خطأ الخادم إلى العربية.
 *
 * الخادم يعيد رموزًا إنجليزية ثابتة (`permission_required: users.manage`،
 * `daily_quota_exceeded`…) وكانت تظهر للمستخدم كما هي. الترجمة هنا لا في الخادم
 * عمدًا: قاعدة المشروع أن رسائل السجلّ إنجليزية (نافذة PowerShell تقلب العربية)
 * وما يراه المستخدم عربيّ — والترجمة في الواجهة تحقّق الاثنين، كما أنها لا تكسر
 * الاختبارات التي تؤكّد على الرمز ولا تحتاج إعادة تشغيل الخادم.
 *
 * الرمز غير المعروف يُعاد كما هو: لا نبتلع رسالة لم نتوقّعها.
 */

import { permissionNameAr } from "@/lib/permissionNames";

/** رموز الخادم الثابتة ← نصّها العربيّ */
export const API_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // ── الدخول والجلسة ──
  json_output_not_found: "لا يوجد تفريغ أصليّ (JSON) لهذه المهمة.",
  rebuild_disabled: "إعادة البناء من الأصل غير مسموحة — يفعّلها المدير من الإعدادات.",
  // ── صوت البثّ عبر الخادم (2026-10-20): لماذا لم يُخرج الرابط صوتًا ──
  stream_video_unavailable: "الفيديو غير متاح على يوتيوب",
  stream_video_private: "الفيديو خاصّ",
  stream_live_ended: "البثّ انتهى أو تسجيله غير متاح",
  stream_live_not_started: "البثّ لم يبدأ بعد",
  stream_no_audio: "لم يُخرج الرابط صوتًا",
  stream_first_audio_timeout: "لم يصل صوت خلال المهلة",
  stream_audio_launch_failed: "تعذّر تشغيل أداة استخراج الصوت على الخادم",
  speaker_removal_disabled: "حذف أسماء المتحدّثين غير مسموح — يفعّله المدير من الإعدادات.",
  replacements_invalid: "قاعدة تصحيح غير صالحة — الخطأ مطلوب، وكلّ خانة 100 حرف على الأكثر.",
  replacements_duplicate: "الخطأ نفسه مكرَّر في أكثر من قاعدة.",
  replacements_too_many: "عدد القواعد أكبر من الحدّ (200).",
  missing_credentials: "يرجى إدخال اسم المستخدم/البريد وكلمة المرور.",
  invalid_credentials: "بيانات الدخول غير صحيحة.",
  account_locked: "الحساب مقفل بسبب محاولات دخول فاشلة. راجع المدير.",
  user_has_no_password_hash: "هذا الحساب بلا كلمة مرور. راجع المدير.",
  current_password_wrong: "كلمة المرور الحالية غير صحيحة.",
  password_too_short: "كلمة المرور قصيرة — 8 أحرف على الأقل.",
  password_too_weak: "كلمة المرور ضعيفة — يجب أن تحتوي حرفًا ورقمًا على الأقل.",
  missing_token: "انتهت الجلسة. سجّل الدخول من جديد.",
  token_revoked: "انتهت الجلسة. سجّل الدخول من جديد.",
  invalid_token: "انتهت الجلسة. سجّل الدخول من جديد.",
  user_inactive: "الحساب مجمّد. راجع المدير.",

  // ── الصلاحيات ──
  admin_required: "هذه العملية للمديرين.",
  super_admin_required: "هذه العملية للمدير وحده.",
  forbidden: "لا صلاحية لهذه العملية.",
  permission_denied: "لا صلاحية للوصول إلى هذا المسار.",
  forbidden_session: "لا صلاحية على هذه الجلسة.",
  only_super_admin_can_modify_permissions: "تعديل الصلاحيات للمدير وحده.",
  cannot_modify_super_admin_permissions: "لا يمكن تعديل صلاحيات المدير.",
  super_admin_only_permissions: "بعض الصلاحيات المحدّدة «للمدير فقط» ولا تُمنح لدور آخر.",
  invalid_permission_codes: "رموز صلاحيات غير معروفة.",
  only_super_admin_can_assign_super_admin: "منح دور المدير للمدير وحده.",
  only_super_admin_can_change_super_admin_role: "تغيير دور المدير للمدير وحده.",
  only_super_admin_can_modify_super_admin: "حساب المدير لا يعدّله أو يحذفه إلا مدير.",
  invalid_role: "دور غير معروف.",

  // ── المستخدمون ──
  user_not_found: "المستخدم غير موجود.",
  username_exists: "اسم المستخدم مستعمل.",
  email_exists: "البريد مستعمل.",
  cannot_demote_last_admin: "لا يمكن إنزال آخر مدير.",
  cannot_deactivate_last_admin: "لا يمكن تجميد آخر مدير.",
  cannot_delete_self: "لا يمكنك حذف حسابك.",
  cannot_delete_last_admin: "لا يمكن حذف آخر مدير.",

  // ── المهامّ والتفريغ ──
  job_not_found: "المهمة غير موجودة.",
  empty_job_ids: "لم تُحدَّد أيّ مهمة.",
  cannot_delete_running_job: "لا يمكن حذف مهمة قيد التنفيذ.",
  only_failed_jobs_can_retry: "إعادة التشغيل للمهامّ الفاشلة فقط.",
  job_already_finished: "المهمة انتهت.",
  can_only_change_priority_for_queued_jobs: "تغيير الأولوية للمهامّ في الطابور فقط.",
  invalid_status: "حالة غير معروفة.",
  invalid_date_format: "صيغة التاريخ غير صحيحة.",
  program_not_allowed: "لا صلاحية لاستعمال هذا المحرّك.",
  program_unavailable: "هذا المحرّك غير متاح حاليًّا.",
  daily_quota_exceeded: "تجاوزت حصّتك اليومية.",
  transcribe_failed: "فشل التفريغ.",
  no_output_text: "لا يوجد نصّ ناتج.",
  empty_json_output: "الناتج فارغ.",
  no_transcript_text: "لا يوجد نصّ تفريغ.",
  output_not_found: "ملفّ الناتج غير موجود.",
  transcript_cache_storage_unavailable: "تخزين النصوص غير متاح حاليًّا.",

  // ── الملفّات والمسارات ──
  file_not_found: "الملفّ غير موجود.",
  file_not_found_on_disk: "الملفّ غير موجود على القرص.",
  file_missing: "الملفّ مفقود.",
  file_too_large: "الملفّ أكبر من الحدّ المسموح.",
  invalid_extension: "امتداد غير مسموح.",
  invalid_mime: "نوع ملفّ غير مسموح.",
  invalid_path: "مسار غير صالح.",
  path_out_of_root: "المسار خارج المجلّد المسموح.",
  path_not_allowed: "المسار غير مسموح.",
  path_not_found: "المسار غير موجود.",
  not_a_directory: "المسار ليس مجلّدًا.",
  no_browse_roots_configured: "لم تُضبَط مجلّدات التصفّح في الإعدادات.",
  no_file_path: "لم يُحدَّد مسار ملفّ.",
  folder_path_required: "مسار المجلّد مطلوب.",
  folder_not_allowed: "المجلّد غير مسموح.",
  folder_not_found: "المجلّد غير موجود.",
  paths_required: "المسارات مطلوبة.",
  file_linked_to_active_jobs: "الملفّ مرتبط بمهامّ قيد التنفيذ.",
  file_linked_to_jobs: "الملفّ مرتبط بمهامّ — احذف مهامّه أوّلًا (أو أفرغها من السلّة قبله).",
  logo_type_not_allowed: "نوع الصورة غير مسموح: PNG أو JPEG أو WebP (وICO لأيقونة التبويب).",
  logo_too_large: "الصورة أكبر من 2 ميغابايت.",
  logo_file_locked: "تعذّر استبدال الشعار: ملفّه مفتوح في برنامج آخر — أغلقه وأعد المحاولة.",
  failed_to_prepare_dir: "تعذّر تجهيز المجلّد.",
  write_error: "تعذّرت الكتابة.",
  overwrite_error: "تعذّر الاستبدال.",

  // ── التنزيل والمصادر ──
  url_required: "الرابط مطلوب.",
  invalid_url_scheme: "رابط غير مدعوم.",
  unsupported_source_type: "نوع مصدر غير مدعوم.",
  download_failed: "فشل التنزيل.",
  download_timeout: "انتهت مهلة التنزيل.",
  yt_dlp_not_installed: "أداة التنزيل غير مثبّتة على الخادم.",

  // ── التسجيلات المجدولة ──
  recording_id_required: "رقم التسجيل مطلوب.",
  recording_not_found: "التسجيل غير موجود.",
  recording_not_ready: "التسجيل لم يكتمل بعد.",
  recording_has_no_file: "التسجيل بلا ملفّ.",
  original_not_found: "الملفّ الأصليّ غير موجود.",
  // what runs on each channel (2026-10-23) — the screens build the full «who and where» text from /rt/claims
  channel_busy: "يعمل تفريغ لهذه القناة في مكان آخر",
  page_outdated: "صفحتك قديمة — أعد تحميلها (Ctrl+F5) ثمّ ابدأ التفريغ من جديد",
  claim_required: "لم تُحجز القناة لهذا التفريغ — أعد تحميل الصفحة وابدأ من جديد",
  manual_recording_removed: "«التسجيل اليدوي ساعةً بساعة» أُزيل — اختر مدّة أو تسجيلًا مجدولًا",
  claim_not_found: "انتهى حجز القناة",
  claim_not_yours: "هذا التفريغ ليس من هذه النافذة",
  claim_stop_forbidden: "إيقاف عمل مستخدم آخر يحتاج صلاحيّة إدارة كلّ جلسات البثّ",
  invalid_kind: "نوع القناة غير صالح",

  // ── البثّ المباشر ──
  session_not_found: "الجلسة غير موجودة.",
  invalid_session_id: "رقم جلسة غير صالح.",
  invalid_rt_session_id: "رقم جلسة غير صالح.",
  invalid_path_outside_rt_root: "المسار خارج مجلّد البثّ.",
  temp_key_rate_limited: "طلبات كثيرة جدًّا. انتظر قليلًا ثمّ أعد المحاولة.",
  temp_key_error: "تعذّر الحصول على مفتاح مؤقّت.",
  invalid_segment_range: "نطاق مقاطع غير صالح.",
  empty_segment_text: "نصّ المقطع فارغ.",
  segments_batch_too_large: "عدد المقاطع أكبر من المسموح في طلب واحد.",
  append_failed: "تعذّرت الإضافة.",
  repair_failed: "تعذّر الإصلاح.",

  // ── النشر ──
  content_required: "المحتوى مطلوب.",
  wordpress: "فشل النشر إلى ووردبريس.",
  wordpress_auth: "بيانات ووردبريس غير صحيحة.",
  facebook: "فشل النشر إلى فيسبوك.",
  telegram: "فشل النشر إلى تيليجرام.",
  whatsapp: "فشل النشر إلى واتساب.",
  x: "فشل النشر إلى X.",

  // ── رصد السوشيال ──
  media_watch_url_invalid: "الرابط غير صالح — يجب أن يكون رابطًا كاملًا لصفحة أو منشور.",
  media_watch_url_platform_mismatch: "الرابط ليس على المنصّة المختارة.",
  media_watch_platform_invalid: "اختر المنصّة من القائمة.",
  media_watch_platform_url_invalid: "رابط المنصّة غير صالح — مثل https://www.facebook.com",
  media_watch_platform_locked: "لا تُغيَّر منصّة صفحة لها منشورات.",
  media_watch_page_exists: "هذه الصفحة مرصودة من قبل.",
  media_watch_page_not_found: "الصفحة المرصودة غير موجودة.",
  media_watch_page_has_posts: "لا يمكن حذف صفحة لها منشورات — انقل منشوراتها إلى السلة أوّلًا.",
  media_watch_page_inactive: "الصفحة معطّلة — فعّلها قبل إدخال منشور لها.",
  media_watch_page_in_trash: "صفحة هذا المنشور في السلة — استعدها أوّلًا.",
  media_watch_post_exists: "هذا المنشور مُدخَل من قبل.",
  media_watch_post_not_found: "المنشور غير موجود.",
  media_watch_text_required: "نصّ المنشور مطلوب.",
  media_watch_text_too_long: "نصّ المنشور أطول من المسموح.",
  media_watch_published_at_invalid: "تاريخ النشر غير صالح.",
  media_watch_screenshot_type: "اللقطة يجب أن تكون صورة JPG أو PNG أو WEBP.",
  media_watch_screenshot_too_large: "حجم اللقطة يتجاوز 10 ميغابايت.",
  media_watch_screenshots_too_many: "الحدّ 10 لقطات للمنشور.",
  media_watch_report_rejected: "تعذّر استلام البلاغ — أعد المحاولة بعد لحظات.",
  media_watch_report_rate_limited: "أرسلت بلاغات كثيرة خلال وقت قصير — أعد المحاولة لاحقًا.",
  media_watch_report_not_found: "لا يوجد بلاغ بهذا الرمز.",
  media_watch_report_closed: "فُرز هذا البلاغ من قبل.",
  media_watch_report_status_invalid: "حالة بلاغ غير معروفة.",
  media_watch_screenshot_missing: "لا توجد لقطة لهذا المنشور.",
  media_watch_page_category_invalid: "اختر تصنيف الصفحة من القائمة.",
  media_watch_category_kind_invalid: "نوع القائمة غير معروف.",
  media_watch_category_exists: "هذا الاسم موجود في القائمة.",
  media_watch_category_not_found: "العنصر غير موجود في القائمة.",
  media_watch_category_code_invalid: "النوع أو الدرجة ليس من القائمة المفعَّلة.",
  media_watch_ai_failed: "تعذّر الاتصال بنموذج الذكاء الاصطناعي — أعد المحاولة لاحقًا.",
  media_watch_provider_invalid: "مزوّد التحليل غير معروف.",
  media_watch_temperature_invalid: "درجة الحرارة يجب أن تكون بين 0 و0.5.",
  media_watch_extension_version_not_bumped: "تغيّرت الإضافة دون رفع رقم إصدارها — أبلغ المسؤول التقنيّ.",
  media_watch_extension_missing: "ملفّات الإضافة غير موجودة على الخادم.",
  media_watch_example_needs_review: "المثال يكون من حالة حكم عليها مراجِع — احكم عليها أوّلًا.",
  media_watch_prompt_version_not_found: "نسخة البرومبت غير موجودة.",
  media_watch_ai_bad_response: "ردّ نموذج الذكاء الاصطناعي غير مفهوم — أعد التحليل.",
  media_watch_case_reviewed: "رُوجعت هذه الحالة — لا يُعاد تحليلها بعد المراجعة.",
  media_watch_case_status_invalid: "حالة غير معروفة.",
  media_watch_case_not_found: "الحالة غير موجودة.",
  media_watch_review_decision_invalid: "القرار يجب أن يكون اعتمادًا أو رفضًا.",
  media_watch_review_codes_required: "الاعتماد يحتاج نوع التحريض ودرجته.",
  media_watch_case_not_reviewed: "التقرير لما حكم عليه مراجِع فقط — الحالة بانتظار المراجعة.",
  media_watch_verdict_invalid: "حكم غير معروف.",
  media_watch_period_invalid: "الفترة غير صالحة — تاريخ البداية قبل النهاية، وسنة على الأكثر.",

  // ── عامّ ──
  not_found: "غير موجود.",
  entity_name_required: "الاسم مطلوب.",
  tab_required: "التبويب مطلوب.",
  unsupported_feature: "هذه الميزة غير مدعومة.",
  ai_empty_result: "لم يُرجع المزوّد نتيجة — أُبقيت النتيجة السابقة.",
  ai_format_too_short: "لم يُحفظ لأنّه اختصر النصّ (أقلّ من الحدّ الأدنى في الإعدادات).",
  ai_format_too_long: "لم يُحفظ لأنّه أطال النصّ (أكثر من الحدّ الأعلى في الإعدادات).",
  ai_format_speakers_lost: "لم يُحفظ لأنّه حذف أسطر متحدّثين.",
  // «تفاصيل المعالجة» — النتائج المحفوظة لكلّ مزوّد (2026-10-14)
  ai_search_failed: "تعذّر البحث الذكيّ: لم يُجب المزوّد — أعد المحاولة أو اختر مزوّدًا آخر.",
  run_not_found: "لم يُعثر على هذا التشغيل — ربّما حُذف لأنّ تشغيلات أحدث حلّت محلّه.",
  run_has_no_result: "هذا التشغيل لم يُحفظ له نصّ (فشل أو رُفض أو سبق التحديث)، فلا يُعتمد.",
  unsupported_tab: "لا يمكن اعتماد نتيجة هذا التبويب.",
  ai_article_too_short: "لم يُحفظ الخبر المفصّل لأنّه أقصر من نصف الطول المطلوب — بقيت النتيجة السابقة.",
  ai_edit_too_short: "لم يُحفظ التحرير لأنّه اختصر النصّ (أقلّ من الحدّ الأدنى في الإعدادات).",
  ai_edit_too_long: "لم يُحفظ التحرير لأنّه أطال النصّ (أكثر من الحدّ الأعلى في الإعدادات).",
  ai_truncated: "قُطع ردّ المزوّد عند حدّ الطول فلم يُحفظ — أُبقيت النتيجة السابقة.",
  unsupported_platform: "هذه المنصّة غير مدعومة.",
  internal_error: "خطأ داخليّ في الخادم.",
  db_insert_error: "تعذّر الحفظ في قاعدة البيانات.",
};

/** `permission_required: users.manage` → «تحتاج صلاحية: إدارة المستخدمين» */
function translatePermissionRequired(detail: string): string | null {
  const m = /^permission_required:\s*(.+)$/.exec(detail.trim());
  if (!m) return null;
  // قد تأتي عدّة رموز مفصولة بـ"|" (حارس ميديا بلص: يكفي إحداها)
  const names = m[1]
    .split("|")
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c) => `«${permissionNameAr(c)}»`);
  if (!names.length) return "لا صلاحية لهذه العملية.";
  const joined = names.length === 1 ? names[0] : `${names.join(" أو ")}`;
  return `تحتاج صلاحية: ${joined}`;
}

/** أسماء حقول شائعة في رسائل «X is required» */
const FIELD_NAMES_AR: Readonly<Record<string, string>> = {
  name: "الاسم",
  text: "النصّ",
  url: "الرابط",
  ids: "العناصر",
  file: "الملفّ",
  platform_id: "المنصّة",
  content: "المحتوى",
  title: "العنوان",
  query: "كلمة البحث",
};

/**
 * رسائل نثرية إنجليزية في الخادم (لا رموز): «name is required»،
 * «missing WP_SITE_URL»، «too_many_job_ids_max_50»، «job_status=running».
 * قياسها في `_check_error_map.py` — 13 نمطًا، كلّها مغطّاة هنا.
 */
function translateProse(raw: string): string | null {
  // "name is required" / "ids required" / "file field is required"
  const req = /^([a-z_][a-z0-9_]*)(?:\s+field)?\s+(?:is\s+)?required\b/i.exec(raw);
  if (req) {
    const f = FIELD_NAMES_AR[req[1].toLowerCase()] || req[1];
    const tail = /for\s+(\S+)\s*$/.exec(raw);
    return tail ? `${f} مطلوب مع ${tail[1]}.` : `${f} مطلوب.`;
  }
  // "date_from/date_to required for custom range"
  if (/^date_from\/date_to\s+required/i.test(raw)) {
    return "حدّد تاريخ البداية والنهاية للمدى المخصّص.";
  }
  // "missing WP_SITE_URL" / "missing WordPress credentials"
  const miss = /^missing\s+(.+)$/i.exec(raw);
  if (miss) return `إعداد ناقص على الخادم: ${miss[1]}`;
  // "job_status=running"
  const st = /^job_status=(.+)$/i.exec(raw);
  if (st) return `لا يمكن تنفيذ ذلك والمهمة في حالة: ${st[1]}`;
  // "too_many_job_ids_max_50"
  const many = /^too_many_job_ids_max_(\d+)$/i.exec(raw);
  if (many) return `عدد المهامّ أكبر من المسموح في طلب واحد (${many[1]}).`;
  // "too_many_attempts. Try again in 42 seconds."
  const att = /^too_many_attempts\.?\s*(?:Try again in\s+(\d+)\s*seconds?\.?)?/i.exec(raw);
  if (att) {
    return att[1]
      ? `محاولات كثيرة. أعد المحاولة بعد ${att[1]} ثانية.`
      : "محاولات كثيرة. أعد المحاولة لاحقًا.";
  }
  return null;
}

/** حفظ البرومبت (2026-10-20): ما بين القوسين يُفحص قبل الحفظ — `prompt_bad_braces` · `prompt_unknown_placeholder: x` · `prompt_missing_placeholder: x` */
function translatePromptCheck(raw: string): string | null {
  if (raw === "prompt_bad_braces") return "في البرومبت قوس { أو } مفرد — إن كان نصًّا فاكتبه مضاعفًا {{ أو }}. لم يُحفظ.";
  const unknown = /^prompt_unknown_placeholder:\s*(.+)$/.exec(raw);
  if (unknown) return `في البرومبت {${unknown[1]}} وليس من متغيّرات هذا التبويب — إن كان نصًّا فاكتبه بقوسين مضاعفين {{ }}. لم يُحفظ.`;
  const missing = /^prompt_missing_placeholder:\s*(.+)$/.exec(raw);
  if (missing) return `البرومبت يجب أن يحتوي {${missing[1]}} — مكانه يوضع النصّ المرسل. لم يُحفظ.`;
  return null;
}

/** ترجمة رمز واحد؛ يُعاد كما هو إن لم يكن معروفًا */
export function translateApiCode(detail: string): string {
  const raw = (detail || "").trim();
  if (!raw) return "";
  const promptCheck = translatePromptCheck(raw);
  if (promptCheck) return promptCheck;
  const perm = translatePermissionRequired(raw);
  if (perm) return perm;
  const exact = API_ERROR_MESSAGES[raw];
  if (exact) return exact;
  // رموز بلاحقة: `download_failed:…`, `db_insert_error: …`, `invalid_status, allowed: …`
  const head = /^([a-z][a-z0-9_]*)\s*[:,]/.exec(raw);
  if (head) {
    const base = API_ERROR_MESSAGES[head[1]];
    if (base) return base;
  }
  const prose = translateProse(raw);
  if (prose) return prose;
  return raw;
}

/**
 * الرمز الخام كما أرسله الخادم، بلا ترجمة.
 * لِما يُقارَن بالرموز (`detail.includes("file_too_large")`) أو يُنسخ كتفصيل تقنيّ —
 * الترجمة هناك تكسر المقارنة.
 */
export function apiErrorCode(e: any, fallback = ""): string {
  const data = e?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail != null && typeof detail !== "string") return JSON.stringify(detail);
  return e?.message || fallback;
}

/**
 * رسالة عربية من أيّ خطأ axios.
 * @param fallback ما يُعرض إن لم يكن في الخطأ نصّ مفيد
 */
export function apiErrorMessage(e: any, fallback = "حدث خطأ غير متوقّع"): string {
  const data = e?.response?.data;
  if (typeof data === "string" && data.trim()) return translateApiCode(data);
  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim()) return translateApiCode(detail);
  if (Array.isArray(detail)) {
    const parts = detail.map((v: any) => v?.msg || String(v)).filter(Boolean);
    if (parts.length) return parts.join(" • ");
  }
  if (e?.response?.status === 403) return "لا صلاحية لهذه العملية.";
  if (e?.response?.status === 401) return "انتهت الجلسة. سجّل الدخول من جديد.";
  // رسالة axios الإنجليزية ليست أفضل من نصّنا
  if (e?.message === "Network Error") return "تعذّر الاتصال بالخادم.";
  return e?.message || fallback;
}
